// src/pages/Consignacion/Tabs/TabReposicion.js
//
// Gestión del stock en consignación.
//
// El stock mínimo de cada artículo es el que siempre debe estar disponible;
// la diferencia con el stock actual es lo que hay que comprarle al proveedor.
// En SAP vive en el MinimalStock de la ficha del almacén: el
// MaximalStock no servía porque es un tope de entrada y bloqueaba las
// recepciones grandes (ver el comentario de back/route/consignacion.js).
// La pantalla muestra primero el resumen por proveedor (que es la unidad de
// compra) y debajo el detalle artículo por artículo con el semáforo.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { useToast } from '../../../components/Toast/ToastProvider';
import { useAuth } from '../../../hooks/useAuth';
import { formatMoney, formatNumber, formatFecha } from '../../../utils/formato';
import '../Consignacion.css';

const ETIQUETAS = {
  ok:         { texto: 'Completo',    icono: 'bi-check-circle-fill' },
  reponer:    { texto: 'Reponer',     icono: 'bi-exclamation-circle-fill' },
  quiebre:    { texto: 'Sin stock',   icono: 'bi-x-circle-fill' },
  sobrestock: { texto: 'Sobre stock', icono: 'bi-arrow-up-circle-fill' }
};

const Semaforo = ({ estado }) => {
  const { texto, icono } = ETIQUETAS[estado] || ETIQUETAS.reponer;
  return (
    <span className={`semaforo semaforo-${estado}`}>
      <span className="punto" />
      <i className={`bi ${icono}`} />
      {texto}
    </span>
  );
};

const TabReposicion = () => {
  const { showToast } = useToast();
  // Un lector ve todo el stock pero no puede generar ordenes.
  const { puedeOperar } = useAuth();

  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  // Dimensiones con las que se imputa la orden. Las fija el backend; acá sólo
  // se muestran, para que quien genera la reposición vea a dónde va imputada.
  const [dimensionesSAP, setDimensionesSAP] = useState([]);
  const [dimensionesFaltantes, setDimensionesFaltantes] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroProveedor, setFiltroProveedor] = useState('todos');

  // Proveedor abierto en el modal de compra.
  const [proveedorActivo, setProveedorActivo] = useState(null);
  // Cantidades editadas por el usuario, por código de artículo.
  const [cantidades, setCantidades] = useState({});
  // Búsqueda dentro del modal: con 60+ líneas encontrar una a mano es inviable.
  const [busquedaModal, setBusquedaModal] = useState('');
  // Oculta las líneas cuya brecha ya cubre una orden en curso. Arranca en false:
  // esconderlas por defecto fue lo que hizo pensar que la orden se estaba
  // armando sin los artículos a reponer.
  const [ocultarPedidas, setOcultarPedidas] = useState(false);
  // Modal con el detalle de los artículos sin precio.
  const [verSinPrecio, setVerSinPrecio] = useState(false);
  // Modal con el detalle de los artículos que están por encima del mínimo.
  const [verSobrestock, setVerSobrestock] = useState(false);
  const [generando, setGenerando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const { data } = await api.get('/consignacion/stock');
      setDatos(data);
    } catch (err) {
      console.error('Error cargando consignación:', err);
      setError(err.response?.data?.error || 'No se pudo cargar el stock de consignación.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    // Si falla, el formulario cae a campos de texto: no vale la pena romper
    // toda la pantalla porque no se pudieron leer las dimensiones.
    api.get('/consignacion/dimensiones')
      .then(({ data }) => {
        setDimensionesSAP(data.dimensiones || []);
        setDimensionesFaltantes(data.faltantes || []);
      })
      .catch(err => console.error('No se pudieron cargar las dimensiones:', err));
  }, []);

  // --- Modal de compra ---
  const abrirProveedor = (proveedor) => {
    const lineas = lineasDe(proveedor.cardCode);
    // Se precarga lo SUGERIDO, que descuenta lo que ya está pedido en una orden
    // aprobada. Queda editable: el comprador puede ajustarlo antes de generar.
    setCantidades(Object.fromEntries(lineas.map(a => [a.codigo, a.sugerido])));
    setProveedorActivo(proveedor);
  };

  const cerrarModal = () => {
    setProveedorActivo(null);
    setCantidades({});
    setBusquedaModal('');
    setOcultarPedidas(false);
  };

  /**
   * Las líneas candidatas de un proveedor: TODO lo que está por debajo del
   * mínimo, sea rojo o amarillo.
   *
   * No se filtra por `sugerido > 0`. Un artículo cuya brecha ya está cubierta
   * por una orden en curso entra igual a la lista, pero con cantidad 0 y el
   * número de OC a la vista: esconderlo obligaba a salir de la pantalla para
   * entender por qué faltaba, y dejaba sin decisión al comprador que igual
   * quiere pedirlo (porque la entrega viene demorada, por ejemplo).
   */
  const lineasDe = (cardCode) => (datos?.articulos || [])
    .filter(a => a.proveedor === cardCode && a.aComprar > 0)
    // Primero lo que hay que pedir; lo ya cubierto queda abajo.
    .sort((x, y) => (y.sugerido - x.sugerido) || (y.aComprar - x.aComprar));

  const lineasDelModal = proveedorActivo ? lineasDe(proveedorActivo.cardCode) : [];

  // La búsqueda solo filtra lo que se ve: las cantidades cargadas en líneas
  // ocultas se mantienen y entran igual en la orden.
  const lineasFiltradasModal = lineasDelModal.filter(a => {
    if (ocultarPedidas && a.sugerido === 0) return false;
    if (!busquedaModal.trim()) return true;
    const t = busquedaModal.trim().toLowerCase();
    return a.codigo.toLowerCase().includes(t) || a.descripcion.toLowerCase().includes(t);
  });

  // Acciones masivas: con 60+ líneas, ajustarlas de a una no es opción.
  const ponerSugerido = () =>
    setCantidades(Object.fromEntries(lineasDelModal.map(a => [a.codigo, a.sugerido])));

  const ponerEnCero = () =>
    setCantidades(Object.fromEntries(lineasDelModal.map(a => [a.codigo, 0])));

  const totalModal = lineasDelModal.reduce(
    (acc, a) => acc + (Number(cantidades[a.codigo]) || 0) * a.precio, 0
  );

  const lineasSeleccionadas = lineasDelModal.filter(a => Number(cantidades[a.codigo]) > 0);

  // Las que están por debajo del mínimo pero ya tienen su brecha pedida.
  const lineasYaPedidas = lineasDelModal.filter(a => a.sugerido === 0).length;

  const generarOrden = async () => {
    if (lineasSeleccionadas.length === 0) {
      showToast('No hay líneas con cantidad para generar la orden.', 'warning');
      return;
    }

    setGenerando(true);
    try {
      const { data } = await api.post('/consignacion/orden', {
        proveedor: proveedorActivo.cardCode,
        lineas: lineasSeleccionadas.map(a => ({
          codigo: a.codigo,
          cantidad: Number(cantidades[a.codigo]),
          precio: a.precio
        }))
      });

      showToast(
        `Borrador creado${data.docNum ? ` (N° ${data.docNum})` : ''} con ${data.lineas} líneas.`,
        'success',
        7000
      );
      cerrarModal();
      cargar();
    } catch (err) {
      console.error('Error generando el borrador:', err);
      showToast(err.response?.data?.error || 'No se pudo generar el borrador de la orden.', 'danger', 9000);
    } finally {
      setGenerando(false);
    }
  };

  // Sale de los artículos que ya trajo el endpoint: no hace falta que el
  // backend lo resuma.
  const articulosSinPrecio = (datos?.articulos || []).filter(a => a.sinPrecio);

  // Los que tienen más stock del que deberían, del que más sobra al que menos.
  const articulosSobrestock = (datos?.articulos || [])
    .filter(a => a.estado === 'sobrestock')
    .sort((a, b) => b.excedente - a.excedente);

  /** Copia los códigos al portapapeles, para pegarlos al cargar precios en SAP. */
  const copiarCodigosSinPrecio = async () => {
    const codigos = articulosSinPrecio.map(a => a.codigo).join('\n');
    try {
      await navigator.clipboard.writeText(codigos);
      showToast(`${articulosSinPrecio.length} códigos copiados.`, 'success');
    } catch {
      // El portapapeles falla si la página no está en HTTPS o el navegador lo bloquea.
      showToast('El navegador no permitió copiar. Seleccioná los códigos a mano.', 'warning');
    }
  };

  // --- Filtros de la tabla ---
  // Los proveedores salen del resumen que ya vino armado: así el combo lista
  // exactamente los mismos que las tarjetas de arriba, en el mismo orden.
  const proveedoresFiltro = (datos?.proveedores || [])
    .map(p => ({ cardCode: p.cardCode, cardName: p.cardName }));

  const articulosVisibles = (datos?.articulos || []).filter(a => {
    if (filtroEstado !== 'todos' && a.estado !== filtroEstado) return false;
    if (filtroProveedor !== 'todos' && a.proveedor !== filtroProveedor) return false;
    if (!busqueda.trim()) return true;
    const t = busqueda.trim().toLowerCase();
    return a.codigo.toLowerCase().includes(t)
      || a.descripcion.toLowerCase().includes(t)
      // El nombre del proveedor también busca: es lo primero que alguien tipea
      // cuando lo tiene a la vista en la columna.
      || (a.proveedorNombre || '').toLowerCase().includes(t);
  });

  if (cargando) {
    return (
      <div className="container-fluid">
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2">Cargando stock de consignación...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* El título de la pantalla vive en el contenedor de solapas; acá queda
          sólo lo propio de esta vista. */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-4">
        {datos && (
          <p className="text-muted small mb-0">
            Almacén <strong>{datos.almacen}</strong> · precios de la lista {datos.listaPrecio}
          </p>
        )}
        <button className="btn btn-outline-primary ms-auto" onClick={cargar} disabled={cargando}>
          <i className="bi bi-arrow-clockwise me-1"></i> Actualizar
        </button>
      </div>

      {error && <div className="alert alert-danger mt-4">{error}</div>}

      {datos && (
        <>
          {/* --- Semáforo general --- */}
          <div className="d-flex flex-wrap gap-2 mt-4">
            <span className="chip-resumen">
              <i className="bi bi-boxes"></i> {datos.resumen.total} artículos
            </span>
            <span className="chip-resumen semaforo-ok">
              <span className="punto" /> {datos.resumen.ok} completos
            </span>
            <span className="chip-resumen semaforo-reponer">
              <span className="punto" /> {datos.resumen.reponer} a reponer
            </span>
            <span className="chip-resumen semaforo-quiebre">
              <span className="punto" /> {datos.resumen.quiebre} sin stock
            </span>
            {/* Se muestra siempre, como los otros tres, aunque esté en cero: si
                apareciera sólo cuando hay sobrestock, no habría forma de saber
                que la pantalla lo controla. Clickeable sólo cuando hay algo que
                mostrar — el detalle se abre en su propio modal porque el
                sobrestock no se acciona desde la tabla, no genera compra. */}
            {datos.resumen.sobrestock > 0 ? (
              <button
                type="button"
                className="chip-resumen chip-accionable semaforo-sobrestock"
                onClick={() => setVerSobrestock(true)}
                title="Ver los artículos que superan su stock mínimo"
              >
                <span className="punto" /> {datos.resumen.sobrestock} con sobre stock
                <i className="bi bi-box-arrow-up-right ms-1" />
              </button>
            ) : (
              <span className="chip-resumen semaforo-sobrestock" title="Ninguno supera su stock mínimo">
                <span className="punto" /> 0 con sobre stock
              </span>
            )}
          </div>

          {/* --- Artículos sin precio en la lista de consignación ---
              Se muestra aparte porque no es un problema de stock sino de datos:
              estos artículos entran a la orden con precio 0. */}
          {articulosSinPrecio.length > 0 && (
            <div
              className="card card-sin-precio shadow-sm mt-4"
              onClick={() => setVerSinPrecio(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setVerSinPrecio(true); }}
            >
              <div className="card-body d-flex align-items-center gap-3 py-3">
                <div className="icono-sin-precio">
                  <i className="bi bi-tag-fill"></i>
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex align-items-baseline gap-2 flex-wrap">
                    <span className="numero-sin-precio">{articulosSinPrecio.length}</span>
                    <span className="fw-semibold">
                      {articulosSinPrecio.length === 1 ? 'artículo sin precio' : 'artículos sin precio'}
                    </span>
                    <span className="text-muted small">en la lista {datos.listaPrecio}</span>
                  </div>
                  <small className="text-muted">
                    Se pueden pedir igual, pero salen en la orden con precio 0 y hay que
                    corregirlos en el borrador. Cargales el precio en SAP para evitarlo.
                  </small>
                </div>
                <span className="btn btn-sm btn-outline-warning text-nowrap">
                  <i className="bi bi-list-ul me-1"></i>Ver detalle
                </span>
              </div>
            </div>
          )}

          {/* --- Proveedores: la unidad de compra --- */}
          <h5 className="mt-4 mb-3">A comprar por proveedor</h5>

          {datos.proveedores.length === 0 ? (
            <div className="alert alert-success">
              <i className="bi bi-check-circle me-2"></i>
              No hay nada para reponer: todos los artículos llegan a su stock mínimo.
            </div>
          ) : (
            <div className="row">
              {datos.proveedores.map(p => (
                <div className="col-lg-4 col-md-6 mb-3" key={p.cardCode}>
                  <div
                    className="card card-proveedor shadow-sm h-100"
                    onClick={() => abrirProveedor(p)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') abrirProveedor(p); }}
                  >
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="pe-2">
                          <h6 className="mb-1">{p.cardName}</h6>
                          <code className="text-muted small">{p.cardCode}</code>
                        </div>
                        <i className="bi bi-chevron-right text-muted"></i>
                      </div>

                      {/* El monto es lo que va a salir en la orden: ya tiene
                          descontado lo que está pedido. Que sea cero no es un
                          error, es la respuesta correcta cuando todo lo que
                          falta ya se pidió. */}
                      {p.articulosAComprar > 0 ? (
                        <>
                          <div className="monto mt-3">{formatMoney(p.montoEstimado)}</div>
                          <div className="d-flex flex-wrap gap-3 mt-2 small text-muted">
                            <span><strong>{p.articulosAComprar}</strong> de {p.totalArticulos} artículos</span>
                            <span><strong>{formatNumber(p.unidadesAComprar)}</strong> unidades a pedir</span>
                          </div>
                        </>
                      ) : (
                        <div className="nada-que-pedir mt-3">
                          <i className="bi bi-check-circle me-2"></i>
                          Nada que pedir por ahora
                        </div>
                      )}

                      {/* El faltante contra el mínimo sigue siendo un dato real,
                          pero es contexto: sin esta línea, ver "3 unidades" en un
                          proveedor con 82 artículos en quiebre parece un error. */}
                      {p.articulosConFaltante > p.articulosAComprar && (
                        <div className="small text-muted mt-2">
                          <i className="bi bi-info-circle me-1"></i>
                          {formatNumber(p.unidadesBrecha - p.unidadesAComprar)} unidades
                          en {p.articulosConFaltante - p.articulosAComprar} artículo(s) están
                          por debajo del mínimo pero ya fueron pedidas
                          {p.pendiente?.ordenes?.length > 0 && (
                            <> en {p.pendiente.ordenes.length === 1 ? 'la OC' : 'las OC'}{' '}
                              <strong>{p.pendiente.ordenes.map(o => `#${o.docNum}`).join(', ')}</strong>
                            </>
                          )}
                        </div>
                      )}

                      <div className="d-flex flex-wrap gap-2 mt-3">
                        {p.enQuiebre > 0 && (
                          <span className="semaforo semaforo-quiebre">
                            <span className="punto" /> {p.enQuiebre} sin stock
                          </span>
                        )}
                        {/* El amarillo entra en la orden igual que el rojo: si no
                            se muestra, la OC parece armarse con menos de lo que
                            realmente lleva. */}
                        {p.enReponer > 0 && (
                          <span className="semaforo semaforo-reponer">
                            <span className="punto" /> {p.enReponer} a reponer
                          </span>
                        )}
                        {p.sinPrecio > 0 && (
                          <span className="badge text-bg-warning">
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            {p.sinPrecio} sin precio
                          </span>
                        )}
                      </div>

                      {/* Lo que ya está en curso con este proveedor. Sin esto, entre
                          que se genera la orden y alguien la aprueba, la pantalla
                          muestra los artículos como si no se hubiera pedido nada. */}
                      {p.pendiente?.hayPendientes && (
                        <div className="aviso-pendiente mt-3">
                          <div className="fw-semibold mb-1">
                            <i className="bi bi-hourglass-split me-1"></i>
                            Ya hay pedidos en curso
                          </div>
                          <ul className="mb-0 ps-3">
                            {p.pendiente.borradores.length > 0 && (
                              <li>
                                {p.pendiente.borradores.length} borrador(es) sin convertir
                                <span className="text-muted"> — {p.pendiente.borradores.slice(0, 3).map(b => `#${b.docNum}`).join(', ')}</span>
                              </li>
                            )}
                            {p.pendiente.enAutorizacion.length > 0 && (
                              <li>
                                {p.pendiente.enAutorizacion.length} orden(es) esperando autorización
                                <span className="text-muted"> — {p.pendiente.enAutorizacion.map(b => `#${b.docNum}`).join(', ')}</span>
                              </li>
                            )}
                            {p.pendiente.unidadesEnPedido > 0 && (
                              <li>
                                {formatNumber(p.pendiente.unidadesEnPedido)} unidades pedidas
                                en {p.pendiente.articulosEnPedido} artículo(s),
                                <span className="text-muted"> pendientes de que el proveedor haga la entrada</span>
                                {/* Sin el número de orden el aviso no sirve para
                                    actuar: no se puede ir a mirarla ni reclamarla. */}
                                {p.pendiente.ordenes?.length > 0 && (
                                  <ul className="mb-0 ps-3 list-unstyled">
                                    {p.pendiente.ordenes.map(o => (
                                      <li key={o.docEntry}>
                                        OC <strong>#{o.docNum}</strong> — {formatNumber(o.unidades)} u.
                                        {o.fechaEntrega && <> · entrega {formatFecha(o.fechaEntrega)}</>}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* --- Detalle de artículos --- */}
          <div className="card shadow-sm mt-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h5 className="card-title mb-0">Detalle de artículos</h5>
                <div className="d-flex gap-2 flex-wrap">
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto', maxWidth: '220px' }}
                    value={filtroProveedor}
                    onChange={e => setFiltroProveedor(e.target.value)}
                  >
                    <option value="todos">Todos los proveedores</option>
                    {proveedoresFiltro.map(p => (
                      <option key={p.cardCode} value={p.cardCode}>{p.cardName}</option>
                    ))}
                  </select>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto' }}
                    value={filtroEstado}
                    onChange={e => setFiltroEstado(e.target.value)}
                  >
                    <option value="todos">Todos los estados</option>
                    <option value="quiebre">Sin stock</option>
                    <option value="reponer">A reponer</option>
                    <option value="ok">Completos</option>
                    <option value="sobrestock">Con sobre stock</option>
                  </select>
                  <input
                    type="search"
                    className="form-control form-control-sm"
                    style={{ width: '240px' }}
                    placeholder="Buscar por código o descripción..."
                    value={busqueda}
                    onChange={e => setBusqueda(e.target.value)}
                  />
                </div>
              </div>

              {articulosVisibles.length === 0 ? (
                <p className="text-muted mb-0">No hay artículos que coincidan con el filtro.</p>
              ) : (
                <div className="table-responsive tabla-consignacion">
                  <table className="table table-sm table-hover align-middle">
                    <thead>
                      <tr>
                        <th>Estado</th>
                        <th>Código</th>
                        <th>Descripción</th>
                        <th style={{ minWidth: '150px' }}>Proveedor</th>
                        <th className="text-end">Stock</th>
                        <th className="text-end">Stock mín.</th>
                        <th style={{ minWidth: '110px' }}>Cobertura</th>
                        <th className="text-end">En pedido</th>
                        <th className="text-end">A comprar</th>
                        <th className="text-end">Precio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {articulosVisibles.map(a => (
                        <tr key={a.codigo}>
                          <td><Semaforo estado={a.estado} /></td>
                          <td><code>{a.codigo}</code></td>
                          <td className="small">{a.descripcion}</td>
                          {/* La razón social completa no entra: se recorta y el
                              nombre entero queda en el title. */}
                          <td className="small celda-descripcion" title={a.proveedorNombre}>
                            {a.proveedorNombre}
                          </td>
                          <td className="text-end">{formatNumber(a.stock)}</td>
                          <td className="text-end">{formatNumber(a.stockMinimo)}</td>
                          <td>
                            <div className="barra-cobertura" title={`${a.cobertura}% del stock mínimo`}>
                              <div
                                className={`barra-${a.estado}`}
                                style={{ width: `${Math.min(100, a.cobertura)}%` }}
                              />
                            </div>
                            <small className="text-muted">{a.cobertura}%</small>
                          </td>
                          {/* Ya pedido: el campo Ordered de la ficha de CONSIG.
                              Es mercadería con orden aprobada, pendiente de que
                              el proveedor haga la entrada. */}
                          <td className="text-end">
                            {a.enPedido > 0
                              ? <>
                                  <span
                                    className="badge bg-info-subtle text-info-emphasis fw-normal"
                                    title={(a.ordenes || []).map(o =>
                                      `OC #${o.docNum}: ${o.pendiente} de ${o.pedido} u.`
                                      + (o.fechaEntrega ? ` — entrega ${formatFecha(o.fechaEntrega)}` : '')
                                    ).join('\n') || undefined}
                                  >
                                    {formatNumber(a.enPedido)}
                                  </span>
                                  {/* El número de OC bajo la cantidad: es el dato
                                      con el que se va a SAP o se reclama. */}
                                  {a.ordenes?.length > 0 && (
                                    <span className="d-block text-muted" style={{ fontSize: '.72rem' }}>
                                      {a.ordenes.map(o => `#${o.docNum}`).join(', ')}
                                    </span>
                                  )}
                                </>
                              : <span className="text-muted">—</span>}
                          </td>
                          <td className="text-end fw-semibold"
                              title={a.enPedido > 0
                                ? `Faltan ${a.aComprar} para el mínimo, pero ya hay ${a.enPedido} pedidas`
                                : undefined}>
                            {a.sugerido > 0 ? formatNumber(a.sugerido) : '—'}
                            {a.enPedido > 0 && a.aComprar !== a.sugerido && (
                              <span className="d-block text-muted" style={{ fontSize: '.75rem' }}>
                                brecha {formatNumber(a.aComprar)}
                              </span>
                            )}
                          </td>
                          <td className="text-end">
                            {a.sinPrecio
                              ? <span className="badge text-bg-warning">sin precio</span>
                              : formatMoney(a.precio)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* --- Modal: artículos sin precio ---
          Es una lista para ir a cargar precios en SAP, por eso incluye el botón
          para copiar los códigos. */}
      {verSobrestock && datos && (
        <>
          <div className="modal fade show d-block modal-consignacion" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header align-items-start">
                  <div className="me-3">
                    <div className="text-uppercase text-muted small fw-semibold lh-1 mb-1">
                      Por encima del stock mínimo
                    </div>
                    <h5 className="modal-title mb-1">
                      {articulosSobrestock.length} {articulosSobrestock.length === 1 ? 'artículo' : 'artículos'} con sobre stock
                    </h5>
                    <small className="text-muted">
                      Hay más de lo que debería haber en el almacén. No se reponen, y conviene
                      revisar si corresponde devolverlos al proveedor.
                    </small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setVerSobrestock(false)} aria-label="Cerrar"></button>
                </div>

                <div className="modal-body pt-3">
                  <div className="tabla-modal">
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '96px' }}>Código</th>
                          <th>Descripción</th>
                          <th className="text-end text-nowrap" style={{ width: '110px' }}>Stock / Mínimo</th>
                          <th className="text-end text-nowrap" style={{ width: '90px' }}>Sobrante</th>
                        </tr>
                      </thead>
                      <tbody>
                        {articulosSobrestock.map(a => (
                          <tr key={a.codigo}>
                            <td><code className="small">{a.codigo}</code></td>
                            <td className="small celda-descripcion" title={a.descripcion}>
                              {a.descripcion}
                              <span className="d-block text-muted">{a.proveedorNombre}</span>
                            </td>
                            <td className="text-end small text-muted text-nowrap">
                              {formatNumber(a.stock)} / {formatNumber(a.stockMinimo)}
                            </td>
                            <td className="text-end fw-semibold text-sobrestock">
                              +{formatNumber(a.excedente)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="modal-footer">
                  <button className="btn btn-primary" onClick={() => setVerSobrestock(false)}>
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setVerSobrestock(false)}></div>
        </>
      )}

      {verSinPrecio && datos && (
        <>
          <div className="modal fade show d-block modal-consignacion" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header align-items-start">
                  <div className="me-3">
                    <div className="text-uppercase text-muted small fw-semibold lh-1 mb-1">
                      Precios pendientes
                    </div>
                    <h5 className="modal-title mb-1">
                      {articulosSinPrecio.length} sin precio en la lista {datos.listaPrecio}
                    </h5>
                    <small className="text-muted">
                      Si se piden así, la orden sale con precio 0 en estas líneas.
                    </small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setVerSinPrecio(false)} aria-label="Cerrar"></button>
                </div>

                <div className="modal-body pt-3">
                  <div className="tabla-modal">
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '96px' }}>Código</th>
                          <th>Descripción</th>
                          <th className="text-end text-nowrap" style={{ width: '92px' }}>Stock / Mín.</th>
                          <th className="text-end text-nowrap" style={{ width: '96px' }}>Faltante</th>
                        </tr>
                      </thead>
                      <tbody>
                        {articulosSinPrecio.map(a => (
                          <tr key={a.codigo}>
                            <td><code className="small">{a.codigo}</code></td>
                            <td className="small celda-descripcion" title={a.descripcion}>
                              {a.descripcion}
                            </td>
                            <td className="text-end small text-muted text-nowrap">
                              {formatNumber(a.stock)} / {formatNumber(a.stockMinimo)}
                            </td>
                            <td className="text-end fw-semibold">{formatNumber(a.aComprar)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="modal-footer flex-wrap gap-2">
                  <button
                    className="btn btn-outline-secondary me-auto"
                    onClick={copiarCodigosSinPrecio}
                    type="button"
                  >
                    <i className="bi bi-clipboard me-1"></i>Copiar códigos
                  </button>
                  <button className="btn btn-primary" onClick={() => setVerSinPrecio(false)}>
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setVerSinPrecio(false)}></div>
        </>
      )}

      {/* --- Modal: detalle de compra por proveedor --- */}
      {proveedorActivo && (
        <>
          <div className="modal fade show d-block modal-consignacion" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">

                <div className="modal-header align-items-start">
                  <div className="me-3 min-w-0">
                    <div className="text-uppercase text-muted small fw-semibold lh-1 mb-1">
                      Reposición de consignación
                    </div>
                    <h5 className="modal-title mb-1 text-truncate" title={proveedorActivo.cardName}>
                      {proveedorActivo.cardName}
                    </h5>
                    <code className="text-muted small">{proveedorActivo.cardCode}</code>
                  </div>
                  <button type="button" className="btn-close" onClick={cerrarModal} aria-label="Cerrar"></button>
                </div>

                <div className="modal-body pt-3">

                  {/* Dimensiones: fijas y resueltas por el backend. Se muestran
                      para que se vea a dónde imputa la orden, pero no se editan
                      ni se mandan: aunque el front las enviara, el backend las
                      ignora y usa las suyas. */}
                  <div className="bloque-dimensiones mb-3">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                      <span className="fw-semibold">
                        <i className="bi bi-diagram-3 me-2"></i>
                        Dimensiones de costos
                      </span>
                      <small className="text-muted">fijas · se aplican a todas las líneas</small>
                    </div>

                    {dimensionesSAP.length === 0 ? (
                      <div className="text-muted small fst-italic">Leyendo las dimensiones…</div>
                    ) : (
                      <div className="row g-2">
                        {dimensionesSAP.map(d => (
                          <div className="col-6 col-lg-3" key={d.campo}>
                            <label className="form-label small text-muted mb-1 text-truncate d-block">
                              {d.dimension}
                              {d.calculado && (
                                <span className="ms-1" title="Se calcula según el mes en que se genera la orden">
                                  <i className="bi bi-calendar-event"></i>
                                </span>
                              )}
                            </label>
                            <div className={`dimension-fija ${d.existe ? '' : 'dimension-invalida'}`}>
                              <span className="text-truncate">{d.nombre || d.codigo}</span>
                              {!d.existe && <i className="bi bi-exclamation-triangle-fill ms-1"></i>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {dimensionesFaltantes.length > 0 && (
                      <div className="alert alert-danger py-2 px-3 mt-2 mb-0 small">
                        <i className="bi bi-exclamation-triangle-fill me-1"></i>
                        {dimensionesFaltantes.map(f => `${f.dimension} "${f.codigo}"`).join(', ')} no
                        existe en SAP. El borrador va a ser rechazado: hay que darla de alta antes.
                      </div>
                    )}
                  </div>

                  {/* Barra de acciones: con 60+ líneas hace falta buscar y poder
                      poner todo en cero o volver a la cantidad sugerida. */}
                  <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                    <div className="input-group input-group-sm flex-grow-1" style={{ maxWidth: '280px' }}>
                      <span className="input-group-text bg-white">
                        <i className="bi bi-search text-muted"></i>
                      </span>
                      <input
                        type="search"
                        className="form-control"
                        placeholder="Buscar en la lista..."
                        value={busquedaModal}
                        onChange={e => setBusquedaModal(e.target.value)}
                      />
                    </div>
                    {/* Con un proveedor que tiene una OC grande en curso la lista
                        se llena de líneas en cero. El botón las esconde, pero la
                        vista por defecto las muestra. */}
                    {lineasYaPedidas > 0 && (
                      <button
                        className={`btn btn-sm ${ocultarPedidas ? 'btn-secondary' : 'btn-outline-secondary'}`}
                        onClick={() => setOcultarPedidas(v => !v)}
                        type="button"
                      >
                        <i className={`bi ${ocultarPedidas ? 'bi-eye' : 'bi-eye-slash'} me-1`}></i>
                        {ocultarPedidas
                          ? `Ver las ${lineasYaPedidas} ya pedidas`
                          : `Ocultar ${lineasYaPedidas} ya pedidas`}
                      </button>
                    )}
                    <div className="btn-group btn-group-sm ms-auto">
                      <button className="btn btn-outline-secondary" onClick={ponerSugerido} type="button">
                        <i className="bi bi-magic me-1"></i>Sugerido
                      </button>
                      <button className="btn btn-outline-secondary" onClick={ponerEnCero} type="button">
                        <i className="bi bi-eraser me-1"></i>Limpiar
                      </button>
                    </div>
                  </div>

                  {/* min-width para que en pantallas angostas la tabla scrollee en
                      vez de aplastar las columnas de precio y subtotal. */}
                  <div className="tabla-modal">
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '96px' }}>Código</th>
                          <th>Descripción</th>
                          {/* Rojo y amarillo van los dos en la orden. La columna
                              deja ver de qué se compone antes de generarla. */}
                          <th style={{ width: '104px' }}>Estado</th>
                          <th className="text-end text-nowrap" style={{ width: '92px' }}>Stock / Mín.</th>
                          <th className="text-end" style={{ width: '104px' }}>Cantidad</th>
                          <th className="text-end text-nowrap" style={{ width: '120px' }}>Precio</th>
                          <th className="text-end text-nowrap" style={{ width: '130px' }}>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineasFiltradasModal.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center text-muted py-4">
                              Ningún artículo coincide con la búsqueda.
                            </td>
                          </tr>
                        ) : lineasFiltradasModal.map(a => {
                          const cant = Number(cantidades[a.codigo]) || 0;
                          const modificada = cant !== a.sugerido;
                          return (
                            <tr key={a.codigo} className={cant === 0 ? 'fila-excluida' : ''}>
                              <td><code className="small">{a.codigo}</code></td>
                              <td className="small celda-descripcion" title={a.descripcion}>
                                {a.descripcion}
                              </td>
                              <td>
                                <Semaforo estado={a.estado} />
                                {/* Por qué esta línea viene en cero: la brecha ya
                                    está cubierta por una orden en curso. Se deja
                                    editable — si la entrega viene demorada, el
                                    comprador puede pedirla igual. */}
                                {a.sugerido === 0 && a.enPedido > 0 && (
                                  <span
                                    className="d-block text-muted text-nowrap"
                                    style={{ fontSize: '.72rem' }}
                                    title={`Faltan ${a.aComprar} para el mínimo y ya hay ${a.enPedido} pedidas`}
                                  >
                                    ya pedido
                                    {a.ordenes?.length > 0 && ` · OC ${a.ordenes.map(o => `#${o.docNum}`).join(', ')}`}
                                  </span>
                                )}
                              </td>
                              <td className="text-end small text-muted text-nowrap">
                                {formatNumber(a.stock)} / {formatNumber(a.stockMinimo)}
                              </td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  className={`form-control form-control-sm text-end ${modificada ? 'border-warning' : ''}`}
                                  value={cantidades[a.codigo] ?? ''}
                                  title={modificada ? `Sugerido: ${a.sugerido}` : 'Cantidad sugerida'}
                                  onChange={e => setCantidades(c => ({ ...c, [a.codigo]: e.target.value }))}
                                />
                              </td>
                              <td className="text-end text-nowrap">
                                {a.sinPrecio
                                  ? <span className="badge text-bg-warning">sin precio</span>
                                  : formatMoney(a.precio)}
                              </td>
                              <td className="text-end fw-semibold text-nowrap">
                                {formatMoney(cant * a.precio)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {lineasDelModal.some(a => a.sinPrecio) && (
                    <div className="alert alert-warning py-2 mb-0 mt-3 small">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      Hay artículos sin precio en la lista {datos.listaPrecio}. Se envían con
                      precio 0 y hay que corregirlos en el borrador.
                    </div>
                  )}
                </div>

                <div className="modal-footer flex-wrap gap-2">
                  <div className="me-auto">
                    <div className="d-flex align-items-baseline gap-2">
                      <span className="text-muted small">Total estimado</span>
                      <span className="total-modal">{formatMoney(totalModal)}</span>
                    </div>
                    <small className="text-muted">
                      {lineasSeleccionadas.length} de {lineasDelModal.length} líneas con cantidad
                    </small>
                  </div>
                  <button className="btn btn-outline-secondary" onClick={cerrarModal} disabled={generando}>
                    Cancelar
                  </button>
                  {puedeOperar ? (
                    <button
                      className="btn btn-primary"
                      onClick={generarOrden}
                      disabled={generando || lineasSeleccionadas.length === 0}
                    >
                      {generando
                        ? <><span className="spinner-border spinner-border-sm me-2" />Generando...</>
                        : <><i className="bi bi-file-earmark-plus me-1" />Generar borrador de OC</>}
                    </button>
                  ) : (
                    <span className="text-muted small">
                      <i className="bi bi-eye me-1"></i>
                      Tu rol es de sólo lectura: podés consultar, no generar órdenes.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={cerrarModal}></div>
        </>
      )}
    </div>
  );
};

export default TabReposicion;
